import {Session} from "@m2c2kit/session";
import {ColorDots} from "./index";

const activity = new ColorDots();

// Set game parameters from URL parameters
const urlParams = new URLSearchParams(window.location.search);
const number_of_trials_count = 4; // 시도 횟수
const gameParameters: { [key: string]: string } = {
    "number_of_trials": number_of_trials_count.toString()
};
urlParams.forEach((value, key) => {
    gameParameters[key] = value;
});
activity.setParameters(gameParameters);

const session = new Session({
    activities: [activity],
});

type RgbaColor = [number, number, number, number];

type Location = {
    x: number;
    y: number;
};

type PresentedDot = {
    color_name: string;
    rgba_color: RgbaColor;
    location: Location;
};

type ColorSelected = {
    color_name: string;
    rgba_color: RgbaColor;
};

type DeviceMetadata = {
    userAgent: string;
    devicePixelRatio: number;
    screen: {
        availHeight: number;
        availWidth: number;
        colorDepth: number;
        height: number;
        orientation: {
            type: string;
            angle: number;
        };
        pixelDepth: number;
        width: number;
    };
    webGlRenderer: string;
};

type Trial = {
    document_uuid: string;
    study_id: string | null;
    study_uuid: string | null;
    session_uuid: string;
    activity_uuid: string;
    activity_id: string;
    activity_publish_uuid: string;
    activity_version: string;
    device_timezone: string;
    device_timezone_offset_minutes: number;
    locale: string;
    activity_begin_iso8601_timestamp: string;
    trial_begin_iso8601_timestamp: string;
    trial_end_iso8601_timestamp: string;
    trial_index: number;
    square_side_length: number;
    presented_dots: PresentedDot[];
    color_target_dot_index: number;
    color_selected: ColorSelected;
    color_selected_correct: boolean;
    location_target_dot_index: number;
    location_selected: Location;
    location_selected_delta: number;
    color_selection_response_time_ms: number;
    location_selection_response_time_ms: number;
    quit_button_pressed: boolean;
    device_metadata: DeviceMetadata;
};

type InputData = {
    trials: Trial[];
};

interface FlutterChannel {
    postMessage(message: string): void; // Flutter로 메시지를 전달하는 메서드
}

// 글로벌 window 객체 확장
declare global {
    interface Window {
        FlutterChannel?: FlutterChannel; // Flutter WebView와의 통신 채널
    }
}

function sendDataToFlutter(data: any) {
    console.log("sendDataToFlutter= " + JSON.stringify(data));

    if (window.FlutterChannel && typeof window.FlutterChannel.postMessage === "function") {
        console.log("FlutterChannel is Defined ");
        window.FlutterChannel.postMessage(JSON.stringify(data));
    } else {
        console.error("FlutterChannel is not defined or postMessage is not a function.");
    }
}

session.onActivityData((ev) => {
    try {
        if (ev.data && Array.isArray(ev.data.trials) && number_of_trials_count == ev.data.trials.length) {
            const sessionData = calculateMetrics(JSON.parse(JSON.stringify(ev.data)));
            console.log("원본데이터= " + JSON.stringify(ev.data));

            // Flutter로 데이터 전달
            sendDataToFlutter({
                type: "complete",
                sessionData: sessionData
            });
        }
    } catch (error) {
        console.error("Error processing activity data:", error);
    }
});

type Metrics = {
    color_dot_acc: number;  // 올바른 색상 응답 비율
    dist_acc: number;       // 올바른 위치 응답 비율
    color_swap: number;     // 색상 바인딩 오류 비율
    dist_swap: number;      // 위치 교환 오류 비율
    color_random: number;   // 랜덤 색상 오류 비율
    dist_random: number;    // 랜덤 위치 오류 비율
    color_dot_qual: number; // 품질 관리 (1: 올바른 시도 수, 0: 불일치)
};

function calculateMetrics(inputData: InputData): Metrics {

    // 1. 입력 데이터(inputData)와 trials 배열의 유효성 검사
    if (!inputData || !Array.isArray(inputData.trials)) {
        throw new Error("유효하지 않은 inputData입니다.");
    }
    const trials = inputData.trials;

    // 2. 전체 시도 수 확인 (시도 수가 0이면 모든 지표를 0으로 반환)
    const totalTrials = trials.length;
    const resultData: any = {
        colorDotAcc: 0,
        colorDotTotAcc: 0,
        distAcc: 0,
        colorSwap: 0,
        distSwap: 0,
        colorRandom: 0,
        distRandom: 0,
        colorDotQual: 0,
        colorCorrCnt: 0,
        locCorrCnt: 0,
    }; // 결과 데이터를 저장할 객체

    // 3. 각 trial의 필드 존재 및 타입 검증 후 조건에 맞게 필터링
    //    (필드가 없거나 타입이 다르면 해당 trial은 계산에 포함하지 않음)
    const colorcorrTrials = trials.filter((trial) => {
        // trial.color_selected_correct가 true
        return trial.color_selected_correct;
    });

    const loccorrTrials = trials.filter((trial) => {
        // trial.location_selected_delta가 48이하(점 크기)
        return trial.location_selected_delta <= 48;
    });

    const colorerrorBTrials = trials.filter((trial) => {
        // trial.color_selected가 존재하며, color_selected.color_name이 정답과 불 일치
        return trial.color_selected && trial.color_selected.color_name !== trial.presented_dots[trial.color_target_dot_index].color_name;
    });

    const locswapTrials = trials.filter((trial) => {
        const { location_selected, presented_dots, location_target_dot_index } = trial;

        // 모든 도트 인덱스 (세 개라고 가정)
        const allIndices = [0, 1, 2];
        // target 도트 인덱스를 제외한 인덱스들
        const distractorIndices = allIndices.filter(index => index !== location_target_dot_index);

        // 두 distractor 인덱스
        const [idxA, idxB] = distractorIndices;
        const distanceToDotA = calculateDistance(location_selected, presented_dots[idxA].location);
        const distanceToDotB = calculateDistance(location_selected, presented_dots[idxB].location);

        // console.log(`location_selected와 presented_dots[${idxA}] 사이의 거리:`, distanceToDotA);
        // console.log(`location_selected와 presented_dots[${idxB}] 사이의 거리:`, distanceToDotB);

        // 두 거리가 하나라도 78 이하일 때 오답
        return distanceToDotA <= 78 || distanceToDotB <= 78; // 선택한 점이 다른 점 하고 거리가 78이하면 오류
    });

    const colorerrorRTrials = trials.filter((trial) => {
        // trial.color_selected가 존재하며, color_selected.color_name이 정답과 불 일치
        return trial.color_selected && trial.color_selected.color_name !== trial.presented_dots[trial.color_target_dot_index].color_name;
    });

    const locrandomTrials = trials.filter((trial) => {
        // trial.location_selected_delta가 48초과 오류
        return trial.location_selected_delta > 48;
    });

    const errTrials = trials.filter((trial) => {
        return !trial.color_selected_correct || trial.location_selected_delta > 48; // 위치 || 색상 오답
    });

    // console.log("colorcorrTrials   = " + colorcorrTrials.length   );
    // console.log("loccorrTrials     = " + loccorrTrials.length     );
    // console.log("colorerrorBTrials = " + colorerrorBTrials.length );
    // console.log("locswapTrials     = " + locswapTrials.length     );
    // console.log("colorerrorRTrials = " + colorerrorRTrials.length );
    // console.log("locrandomTrials   = " + locrandomTrials.length   );
    // console.log("totalTrials       = " + totalTrials              );

    // 4. 각 비율 계산 (totalTrials는 0이 아님)
    // 색상 정답 비율 (%) colorcorr
    resultData.colorDotAcc = (colorcorrTrials.length / totalTrials) * 100
    // 위치 정답 비율 (%) loccorr
    resultData.distAcc = (loccorrTrials.length / totalTrials) * 100
    // 색상 오답 비율 (%) colorerrorB
    resultData.colorSwap = (colorerrorBTrials.length / totalTrials) * 100
    // EAS 복합 DV (0~1): 거리 정확도와 색상 오류율을 결합
    resultData.colorDotTotAcc = ((resultData.distAcc / 100) + (1 - resultData.colorSwap / 100)) / 2
    // 위치 오답 비율 (%) locswap
    resultData.distSwap = (locswapTrials.length / totalTrials) * 100
    // 색상 랜덤 오답 비율 (%) colorerrorR
    resultData.colorRandom = (colorerrorRTrials.length / totalTrials) * 100
    // 위치 랜덤 오답 비율 (%) locrandom
    resultData.distRandom = (locrandomTrials.length / totalTrials) * 100
    // 품질 관리: 정해진 횟수(4회)가 모두 제출되면 1, 누락이 있으면 0
    resultData.colorDotQual = trials.length === number_of_trials_count ? 1 : 0;
    // 색상 정답 개수
    resultData.colorCorrCnt = colorcorrTrials.length;
    // 위치 정답 개수
    resultData.locCorrCnt = loccorrTrials.length;


    return resultData;
}

// 두 점 사이의 유클리드 거리를 계산하는 함수
function calculateDistance(pointA: any, pointB: any) {
    const dx = pointA.x - pointB.x;
    const dy = pointA.y - pointB.y;
    return Math.sqrt(dx * dx + dy * dy);
}

session.initialize();
